import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// TV / D-pad navigation engine for FlexWatch.
// - Wire "Watch Now" to a dedicated full-screen player page
// - Arrow keys / TV D-pad move focus spatially across cards & buttons
// - Enter / DPAD_CENTER activates, Back / Backspace / Escape goes back
// - Works on old Android WebViews via keyCode fallbacks (no fancy APIs)
// ---------------------------------------------------------------------------

// Back-handler registry: pages can hook the TV Back button (last registered wins)
const backHandlers = new Set();
export const registerBackHandler = (fn) => {
  backHandlers.add(fn);
  return () => backHandlers.delete(fn);
};
export const unregisterBackHandler = (fn) => backHandlers.delete(fn);

// Pages that manage their own arrows/Enter (e.g. the embed player for seek/volume)
// can disable spatial movement globally. Back handling stays active.
let navigationEnabled = true;
export const setTvNavigationEnabled = (value) => {
  navigationEnabled = Boolean(value);
};

const FOCUSABLE_SELECTOR =
  'a[href], button, input:not([type="hidden"]), select, textarea, [data-focusable]';

const resolveKey = (e) => {
  const k = e.key;
  const code = e.keyCode || e.which || 0;
  if (k === 'ArrowRight' || code === 39) return 'right';
  if (k === 'ArrowLeft' || code === 37) return 'left';
  if (k === 'ArrowUp' || code === 38) return 'up';
  if (k === 'ArrowDown' || code === 40) return 'down';
  if (k === 'Enter' || code === 13 || code === 23) return 'select'; // 23 = Android TV DPAD_CENTER
  if (k === 'Escape' || k === 'Backspace' || code === 27 || code === 8 || code === 461 || code === 10009)
    return 'back';
  return null;
};

const isEditable = (el) =>
  el &&
  (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);

// OK/Enter activation: click the focused element; if it's a non-interactive
// wrapper (e.g. .movie-card div), open its inner link so OK opens the detail page.
export const activateTarget = (el) => {
  if (!el) return;
  if (el.click) el.click();
  const interactive = el.matches('a, button, input, select, textarea, [role="button"]');
  if (!interactive && el.querySelector) {
    const anchor = el.querySelector('a[href]');
    // eslint-disable-next-line no-script-url
    if (anchor && anchor.href && !anchor.href.startsWith('javascript:')) {
      anchor.click();
    }
  }
};

const getFocusables = () =>
  Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
    if (el.disabled || el.getAttribute('aria-hidden') === 'true') return false;
    if (el.getClientRects().length === 0) return false;
    // Skip anything hidden by an inline display/visibility on itself or an ancestor
    // (auto-hiding HUDs keep children in the DOM but must not be D-pad reachable)
    let node = el;
    while (node && node.nodeType === 1) {
      const s = node.style;
      if (s.display === 'none' || s.visibility === 'hidden') return false;
      node = node.parentElement;
    }
    return true;
  });

// Simple directional movement: find the closest element in the pressed direction,
// preferring best alignment when distances are similar.
// Same-card inner buttons are deprioritized (score penalty) so rows/columns move cleanly.
const findBestFocusable = (candidates, current, dir) => {
  const cb = current.getBoundingClientRect();
  const cbCenterX = cb.left + cb.width / 2;
  const cbCenterY = cb.top + cb.height / 2;

  let best = null;
  let bestScore = Infinity;

  for (const el of candidates) {
    if (el === current) continue;
    const r = el.getBoundingClientRect();

    // Determine if candidate is in the intended direction
    let isInDir = false;
    let primaryDist = 0; // distance in the movement direction
    let alignDist = 0; // perpendicular distance (alignment error)
    let sameCard = false;

    if (dir === 'right') {
      isInDir = r.left >= cb.right - 1;
      primaryDist = r.left - cb.right;
      alignDist = Math.abs(r.top + r.height / 2 - cbCenterY);
    } else if (dir === 'left') {
      isInDir = r.right <= cb.left + 1;
      primaryDist = cb.left - r.right;
      alignDist = Math.abs(r.top + r.height / 2 - cbCenterY);
    } else if (dir === 'down') {
      isInDir = r.top >= cb.bottom - 1;
      primaryDist = r.top - cb.bottom;
      alignDist = Math.abs(r.left + r.width / 2 - cbCenterX);
    } else if (dir === 'up') {
      isInDir = r.bottom <= cb.top + 1;
      primaryDist = cb.top - r.bottom;
      alignDist = Math.abs(r.left + r.width / 2 - cbCenterX);
    }

    if (!isInDir || primaryDist <= 0) continue;

    // Deprioritize inner buttons of the same card — prefer staying in the same row/column
    const currentCard = current.closest ? current.closest('[data-card], .movie-card') : null;
    const elCard = el.closest ? el.closest('[data-card], .movie-card') : null;
    if (currentCard && elCard && elCard === currentCard) sameCard = true;

    // Score: primary distance + small weight for alignment + same-card penalty
    const score = primaryDist * 2 + alignDist * 0.5 + (sameCard ? primaryDist * 8 : 0);

    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }

  return best;
};

const scrollIntoViewSafe = (el) => {
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  } catch {
    try {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } catch {
      /* no-op */
    }
  }
};

// Fullscreen helpers with old WebView prefixes
export const isFullscreen = () =>
  Boolean(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement);

export const requestFullscreenSafe = () => {
  const el = document.documentElement;
  try {
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  } catch {
    /* not supported */
  }
};

export const exitFullscreenSafe = () => {
  try {
    if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
  } catch {
    /* not supported */
  }
};

const TvNavigation = () => {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;

  useEffect(() => {
    const handleFocusIn = (e) => {
      document
        .querySelectorAll('.tv-focus')
        .forEach((el) => el !== e.target && el.classList.remove('tv-focus'));
      if (e.target && e.target.classList) e.target.classList.add('tv-focus');
    };
    const handleFocusOut = (e) => {
      if (e.target && e.target.classList) e.target.classList.remove('tv-focus');
    };

    const handleBack = () => {
      // Pages can claim Back first (e.g. close a settings panel) before leaving
      if (backHandlers.size > 0) {
        const fn = Array.from(backHandlers).pop();
        fn();
        return;
      }
      if (isFullscreen()) {
        exitFullscreenSafe();
        return;
      }
      if (window.history.length > 1) navRef.current(-1);
      else navRef.current('/', { replace: true });
    };

    const handleKeyDown = (e) => {
      const intent = resolveKey(e);

      if (isEditable(document.activeElement)) {
        // Never hijack typing, caret movement or select interaction
        return;
      }

      if (!intent) return;

      // Range sliders (seek bar / volume) handle their own Left/Right:
      // don't move focus or preventDefault — their own keydown adjusts the value.
      const activeEl = document.activeElement;
      if (activeEl && activeEl.closest && activeEl.closest('[data-dpad-range]')) {
        return;
      }

      // This page owns its arrow/Enter keys (e.g. embed player seek) — Back still works
      if (!navigationEnabled && intent !== 'back') return;

      e.preventDefault();

      if (intent === 'select') {
        activateTarget(document.activeElement);
        return;
      }

      if (intent === 'back') {
        handleBack();
        return;
      }

      // Arrow keys: spatially move focus
      const list = getFocusables();
      const start = document.activeElement;
      if (!start || !list.includes(start)) {
        if (list.length === 0) return;
        // No current focus: land on the top-left-most visible element
        const sorted = list.slice().sort((a, b) => {
          const ra = a.getBoundingClientRect();
          const rb = b.getBoundingClientRect();
          return ra.top === rb.top ? ra.left - rb.left : ra.top - rb.top;
        });
        const target = sorted[0];
        target.focus({ preventScroll: true });
        scrollIntoViewSafe(target);
        return;
      }

      const next = findBestFocusable(list, start, intent);
      if (next) {
        next.focus({ preventScroll: true });
        scrollIntoViewSafe(next);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('focusin', handleFocusIn, true);
    window.addEventListener('focusout', handleFocusOut, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('focusin', handleFocusIn, true);
      window.removeEventListener('focusout', handleFocusOut, true);
    };
  }, []);

  return null;
};

export default TvNavigation;